import { ConfigFileChangeReview } from '@/features/controller/configuration/components/ConfigFileChangeReview'
import { ConfigReviewDialog } from '@/features/controller/configuration/components/ConfigReviewDialog'
import type { ControllerConfigVersion, ControllerConfigVersionUpdate } from '@/features/controller/configuration/types'
import { TextEditorLanguage } from '@/features/controller/configuration/types'

type ConfigReviewEditsDialogProps = {
  isUpdating?: boolean
  language?: TextEditorLanguage
  modifiedText: string
  onClose: () => void
  onSave: (update: ControllerConfigVersionUpdate, formattedText: string) => void
  onSaveAndRestart: (update: ControllerConfigVersionUpdate, formattedText: string) => void
  open: boolean
  baseVersion: ControllerConfigVersion
  /** The base was replaced with a version saved meanwhile, so the diff may undo someone else's changes */
  baseReplaced?: boolean
  /** A save conflicted and the version saved meanwhile is being fetched */
  isFetchingNewerVersion?: boolean
  comment: string
  onCommentChange: (comment: string) => void
}

/**
 * Format JSON text with proper indentation and spacing before submission to server.
 */
function formatJsonText(text: string): string {
  try {
    const parsed = JSON.parse(text)
    return JSON.stringify(parsed, null, 2)
  } catch {
    // Invalid JSON should not pass here, but in that case return original text
    return text
  }
}

export function ConfigReviewEditsDialog({
  open,
  baseVersion,
  baseReplaced = false,
  isFetchingNewerVersion = false,
  onSave,
  onSaveAndRestart,
  onClose,
  modifiedText,
  comment,
  onCommentChange,
  isUpdating = false,
  language = TextEditorLanguage.ini,
}: Readonly<ConfigReviewEditsDialogProps>) {
  const isJson = language === TextEditorLanguage.json

  const formattedModifiedText = isJson ? formatJsonText(modifiedText) : modifiedText
  // Nothing to save when the draft is the base, say after a conflict with a version that already holds it
  const hasChanges = formattedModifiedText !== baseVersion.value

  const handleSave = () => {
    onSave(
      {
        previousVersionId: baseVersion.id,
        value: formattedModifiedText,
        comment: comment.trim() || undefined,
      },
      formattedModifiedText,
    )
  }

  const handleSaveAndRestart = () => {
    onSaveAndRestart(
      {
        previousVersionId: baseVersion.id,
        value: formattedModifiedText,
        comment: comment.trim() || undefined,
      },
      formattedModifiedText,
    )
  }

  return (
    <ConfigReviewDialog
      comment={comment}
      hasChanges={hasChanges}
      isFetchingNewerVersion={isFetchingNewerVersion}
      isUpdating={isUpdating}
      open={open}
      overwritesNewerVersion={baseReplaced}
      onClose={onClose}
      onCommentChange={onCommentChange}
      onSave={handleSave}
      onSaveAndRestart={handleSaveAndRestart}
    >
      <ConfigFileChangeReview
        baseReplaced={baseReplaced}
        language={language}
        modifiedText={formattedModifiedText}
        originalText={baseVersion.value}
      />
    </ConfigReviewDialog>
  )
}
